import type { MapRegion, WorldCountry } from './worldMapPaths'

/**
 * The world dataset, INJECTED rather than imported.
 *
 * `worldMapPaths.ts` carries ~110 KB of baked path data. It stays a parameter
 * (injected by the sdk.ts entry) rather than an import so that a build without
 * it tree-shakes the geometry out entirely — which is how the tabs were staged
 * behind a second bundle before they shipped on 2026-07-29.
 */
export interface MapDataset {
  viewBox: string
  countries: WorldCountry[]
}

export interface WorldData {
  /** Whole world — the Global tab. */
  global: MapDataset
  /** Europe, clipped to the region, no Greenland/Iceland — the Europe tab. */
  europe: MapDataset
  /** Every id any tab can name, including the five with no geometry. */
  placeById: Record<string, { name: string, region: MapRegion }>
}

// Shared availability resolution for the availability map's three tabs. Pure
// functions over the /api/v1/approval-maps/:slug payload — no React — so the
// USA map, the Global map and the list view all answer "who is live here?"
// through one code path.

export type BookKind = 'sportsbook' | 'dfs' | 'prediction'

export interface MapBook {
  name: string
  /**
   * Exactly one kind per book, set server-side with precedence
   * prediction > dfs > sportsbook. 'prediction' exists because Polymarket is
   * not a sportsbook and does not behave like one: it is the catalog's only
   * denylist operator, so any sport it carries is "live" in ~150 countries.
   * Colouring that as regulated betting overstates it badly.
   */
  kind: BookKind
  brand_color: string | null
  logo_url: string | null
  logo_fill: boolean
  /** Sportsbook mode: the entry is a sport — its slug, for CSS / test hooks. */
  slug?: string | null
  /** Sportsbook mode: light league mark → the plain tile is painted black. */
  logo_dark?: boolean
  affiliate_url: string | null
  states: string[]
  /** ISO alpha-2 allowlist. Optional: absent on an API older than 2026-07-28. */
  countries?: string[]
  /** ISO alpha-2 denylist. */
  blocked_countries?: string[]
  /** Per-country coverage qualifier, e.g. { CA: 'Ontario only' }. */
  country_notes?: Record<string, string>
}

export interface ApprovalMapData {
  slug: string
  title: string
  brand: {
    primary: string | null
    secondary: string | null
    tertiary: string | null
    /** Optional 4th slot — the prediction layer's fill. Absent on an older API. */
    alternate?: string | null
  }
  betting_states: string[]
  dfs_states: string[]
  /** Union of prediction-operator states. Optional on an older API. */
  prediction_states?: string[]
  /** The sport's own international approvals. Near-empty catalog-wide. */
  betting_countries?: string[]
  country_notes?: Record<string, string>
  /** Region tokens the API could not normalize — surfaced, never swallowed. */
  unmapped_country_tokens?: string[]
  books: MapBook[]
  updated_at: string | null
}

/**
 * Map fill categories. The two map families use different subsets, because the
 * question each answers is different (Cole's spec):
 *
 *   USA   'combo' betting + DFS   'bet' betting   'dfs' DFS
 *         'pred'  prediction only  'def' nothing
 *
 * The USA combination bucket is keyed on betting + DFS, NOT on all three
 * layers. Keying it on all three left every betting+DFS state in a league with
 * no prediction carrier sitting under plain "Betting Available", so the
 * combination had no legend entry at all — which is what Cole reported. Its
 * LABEL adapts instead: "Betting, DFS and Prediction" when any state in the
 * bucket also has a prediction market, "Betting and DFS" when none does.
 *
 *   world 'betpred' betting + prediction      'bet' betting
 *         'pred'    prediction only           'def' nothing
 *
 * The world maps deliberately ignore DFS for COLOUR — measured against live
 * data, no country outside the US and Canada has a DFS operator at all, and
 * both of those have sportsbooks too, so a DFS key would describe an empty set.
 * DFS still appears in the popover for those countries.
 */
export type Status = 'combo' | 'betpred' | 'bet' | 'dfs' | 'pred' | 'def'

/**
 * Map regions that are not betting jurisdictions and can never carry a book.
 *
 * No region currently maps here — Antarctica was dropped. The guard is retained
 * because an earlier revision DID include it (to force the Global tab to the USA
 * tab's aspect ratio), and that silently made every denylist book paint it:
 * "live everywhere except the blocked list" is literally true of Antarctica,
 * since no book's blocked list bothers to name it. Re-adding any such landmass
 * needs only a region of 'other' to stay correct.
 */
export const NON_JURISDICTION_REGIONS = new Set<MapRegion>(['other'])

export function isJurisdiction(region: MapRegion): boolean {
  return !NON_JURISDICTION_REGIONS.has(region)
}

/** Books resolved once per payload into set-backed lookups. */
export interface BookIndex {
  book: MapBook
  states: Set<string>
  countries: Set<string>
  blocked: Set<string>
  /** True when the book is a denylist book: live everywhere bar `blocked`. */
  denylist: boolean
}

export function indexBooks(books: MapBook[]): BookIndex[] {
  return books.map(book => ({
    book,
    states: new Set(book.states),
    countries: new Set(book.countries ?? []),
    blocked: new Set(book.blocked_countries ?? []),
    denylist: (book.blocked_countries ?? []).length > 0,
  }))
}

/**
 * Whether a book is live in a country.
 *
 * Deliberately mirrors api/availability.ts:isAllowed() so the map cannot
 * contradict the bet-slip gate: a non-empty denylist means "live everywhere
 * except these", narrowed further by a non-empty allowlist; otherwise the
 * allowlist is the whole story. This is what puts Polymarket — 0 allowed,
 * 37 blocked — on the map as globally live rather than absent.
 */
export function isBookLiveInCountry(idx: BookIndex, iso: string): boolean {
  if (idx.denylist) {
    return !idx.blocked.has(iso) && (idx.countries.size === 0 || idx.countries.has(iso))
  }
  return idx.countries.has(iso)
}

export function isBookLiveInState(idx: BookIndex, stateCode: string): boolean {
  return idx.states.has(stateCode)
}

export interface PlaceBooks {
  sportsbooks: MapBook[]
  dfs: MapBook[]
  prediction: MapBook[]
}

/**
 * Per-state book lists for the USA tab. Sportsbooks only appear in states
 * where the sport itself is approved; DFS apps appear wherever they are live.
 * (Layer semantics confirmed 2026-07-10 — unchanged by this feature.)
 */
export function booksInState(index: BookIndex[], stateCode: string, bettingStates: Set<string>): PlaceBooks {
  return {
    sportsbooks: bettingStates.has(stateCode)
      ? index.filter(i => i.book.kind === 'sportsbook' && isBookLiveInState(i, stateCode)).map(i => i.book)
      : [],
    dfs: index.filter(i => i.book.kind === 'dfs' && isBookLiveInState(i, stateCode)).map(i => i.book),
    // Prediction markets follow the DFS rule, NOT the sportsbook one (Cole's
    // call): they appear wherever the operator is live, without the sport's
    // per-state approval gate. Carriage is the only gate — a prediction
    // operator that does not carry this sport is absent from `index` entirely,
    // so it shows nowhere; one that does carry it shows everywhere it operates.
    prediction: index.filter(i => i.book.kind === 'prediction' && isBookLiveInState(i, stateCode)).map(i => i.book),
  }
}

/**
 * Per-country book lists for the Global tab and list view. Purely carrier
 * driven — there is no country equivalent of the sport-approval gate, because
 * `sport_international` holds 3 values catalog-wide.
 */
export function booksInCountry(index: BookIndex[], iso: string): PlaceBooks {
  return {
    sportsbooks: index.filter(i => i.book.kind === 'sportsbook' && isBookLiveInCountry(i, iso)).map(i => i.book),
    dfs: index.filter(i => i.book.kind === 'dfs' && isBookLiveInCountry(i, iso)).map(i => i.book),
    prediction: index.filter(i => i.book.kind === 'prediction' && isBookLiveInCountry(i, iso)).map(i => i.book),
  }
}

/**
 * United States availability expressed at country level, for the Global map and
 * for any consumer asking "is this sport available in the US".
 *
 * The US is the one country whose answer must NOT come from the carriers'
 * `countries` lists. Those lists describe INTERNATIONAL reach — a US-licensed
 * book has no reason to name its home market in them, and Polymarket actively
 * blocks the US there. Domestic availability lives in the state tables, which is
 * exactly what the USA tab renders. Reading the country lists instead paints the
 * US grey on a sport with 34 approved states and 47 DFS states.
 */
export function booksInUnitedStates(index: BookIndex[], bettingStates: Set<string>): PlaceBooks {
  return {
    // Sportsbooks live in at least one state where the sport is approved, and
    // DFS apps live in at least one state — mirroring the per-state rules.
    sportsbooks: index
      .filter(i => i.book.kind === 'sportsbook' && i.book.states.some(st => bettingStates.has(st)))
      .map(i => i.book),
    dfs: index.filter(i => i.book.kind === 'dfs' && i.book.states.length > 0).map(i => i.book),
    // Ungated, as above.
    prediction: index.filter(i => i.book.kind === 'prediction' && i.book.states.length > 0).map(i => i.book),
  }
}

/**
 * The US shape on the Global/Europe maps. Uses the world taxonomy — betting and
 * prediction only — derived from the state layers rather than from the carriers'
 * international lists (see booksInUnitedStates for why).
 */
export function unitedStatesStatus(
  bettingStates: Set<string>,
  _dfsStates: Set<string>,
  predictionStates: Set<string> = new Set(),
): Status {
  const b = bettingStates.size > 0
  const p = predictionStates.size > 0
  if (b && p) return 'betpred'
  if (b) return 'bet'
  if (p) return 'pred'
  return 'def'
}

/**
 * Country status for the Global and Europe maps: betting and prediction only.
 *
 * DFS is not a category here — see the Status doc. A DFS-only country would
 * fall to 'def'; none exists in the data, and the caller logs it if one ever
 * appears rather than silently painting it as unavailable.
 */
export function statusOf(place: PlaceBooks): Status {
  const b = place.sportsbooks.length > 0
  const p = place.prediction.length > 0
  if (b && p) return 'betpred'
  if (b) return 'bet'
  if (p) return 'pred'
  return 'def'
}

/** True for a country carrying DFS but neither betting nor prediction. */
export function isDfsOnlyCountry(place: PlaceBooks): boolean {
  return place.dfs.length > 0 && place.sportsbooks.length === 0 && place.prediction.length === 0
}

/**
 * State status for the USA map. The betting layer is the curated `sport_states`;
 * the DFS and prediction layers are each the union of their operators' own live
 * states.
 *
 * Categories: betting + DFS, then betting, then DFS, then prediction-only. So
 * 'bet' covers betting with or without a prediction market and 'dfs' covers DFS
 * with or without one — the legend is a summary and the popover carries the
 * exact list.
 */
export function stateStatus(
  code: string,
  betting: Set<string>,
  dfs: Set<string>,
  prediction: Set<string> = new Set(),
): Status {
  const b = betting.has(code)
  const d = dfs.has(code)
  if (b && d) return 'combo'
  if (b) return 'bet'
  if (d) return 'dfs'
  if (prediction.has(code)) return 'pred'
  return 'def'
}

export interface Qualifier {
  book: string
  note: string
}

/**
 * Per-book coverage qualifiers for a country — e.g.
 * `[{ book: 'DraftKings', note: 'Alberta, Ontario only' }]`.
 *
 * Deliberately per-book rather than per-country. A country-level qualifier is
 * wrong whenever books disagree, which is the normal case: DraftKings reaches
 * Canada only through Ontario and Alberta, while Polymarket reaches all of it,
 * so "Canada — Ontario only" would be false while saying nothing at all would
 * overstate DraftKings. Attributing the limit to the book that has it is the
 * only accurate reading.
 */
export function countryQualifiers(index: BookIndex[], iso: string): Qualifier[] {
  const out: Qualifier[] = []
  for (const i of index) {
    if (!isBookLiveInCountry(i, iso)) continue
    const note = i.book.country_notes?.[iso]
    if (note) out.push({ book: i.book.name, note })
  }
  return out
}

/** The qualifier for one book in one country, for tooltips. */
export function bookQualifier(book: MapBook, iso: string): string | null {
  return book.country_notes?.[iso] ?? null
}

export interface RegionRow {
  id: string
  name: string
  books: PlaceBooks
  status: Status
  qualifiers: Qualifier[]
  /** False for places with no 110m geometry — list view only, never painted. */
  mapped: boolean
}

export interface RegionGroup {
  region: MapRegion
  label: string
  rows: RegionRow[]
}

/** Header order for the list view: USA first (Cole's spec), then outward. */
export const REGION_ORDER: { region: MapRegion, label: string }[] = [
  { region: 'usa', label: 'USA' },
  { region: 'canada', label: 'Canada' },
  { region: 'europe', label: 'Europe' },
  { region: 'asia', label: 'Asia' },
  { region: 'middle-east', label: 'Middle East' },
  { region: 'latin-america', label: 'Latin America' },
  { region: 'africa', label: 'Africa' },
  { region: 'oceania', label: 'Oceania' },
]

/**
 * Builds the list view's rows.
 *
 * The USA header lists the 50 states + DC rather than a single "United States"
 * row — the list is the text equivalent of both maps, and state granularity is
 * what the USA map shows. Every other header lists countries.
 *
 * Rows with no availability are omitted by default so the list answers "where
 * can I bet" rather than scrolling through ~140 empty countries. Search widens
 * to every known place, including unavailable ones, so it can also answer
 * "can I bet in X" — those rows render with an explicit not-available state.
 */
export function buildRegionGroups(
  index: BookIndex[],
  bettingStates: Set<string>,
  dfsStates: Set<string>,
  predictionStates: Set<string>,
  usStates: { id: string, name: string }[],
  query: string,
  world: WorldData,
): RegionGroup[] {
  const q = query.trim().toLowerCase()
  const searching = q.length > 0
  const groups: RegionGroup[] = []
  const mappedIds = new Set(world.global.countries.map(c => c.id))

  for (const { region, label } of REGION_ORDER) {
    const rows: RegionGroup['rows'] = []

    if (region === 'usa') {
      for (const s of usStates) {
        if (searching && !s.name.toLowerCase().includes(q) && s.id.toLowerCase() !== q) continue
        const books = booksInState(index, s.id, bettingStates)
        const status = stateStatus(s.id, bettingStates, dfsStates, predictionStates)
        if (!searching && status === 'def' && !books.sportsbooks.length && !books.dfs.length && !books.prediction.length) continue
        rows.push({ id: s.id, name: s.name, books, status, qualifiers: [], mapped: true })
      }
    }
    else {
      for (const [id, place] of Object.entries(world.placeById)) {
        if (place.region !== region) continue
        if (searching && !place.name.toLowerCase().includes(q) && id.toLowerCase() !== q) continue
        const books = booksInCountry(index, id)
        const status = statusOf(books)
        if (!searching && status === 'def') continue
        rows.push({
          id,
          name: place.name,
          books,
          status,
          qualifiers: countryQualifiers(index, id),
          mapped: mappedIds.has(id),
        })
      }
    }

    rows.sort((a, b) => a.name.localeCompare(b.name))
    if (rows.length) groups.push({ region, label, rows })
  }

  return groups
}
